import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Car,
  CheckCircle2,
  Eye,
  EyeOff,
  ImagePlus,
  MapPin,
  Pencil,
  Plus,
  ShoppingCart,
  Trash2,
  Undo2,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, fmtDate, today } from "@/lib/api";
import { COST_CATEGORIES, PURCHASE_PAYMENT_TYPES, PAYMENT_TYPES, carLabel, can } from "@/lib/labels";
import { StatusBadge, Money, TypeBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { CarFormDialog } from "@/pages/Cars";
import { MarkPaidDialog, ReceiveDialog, RefundDialog } from "@/pages/Sales";
import { PayPurchaseDialog } from "@/pages/Purchases";
import { PurchaseDialog, CostDialog, SaleDialog } from "@/components/dialogs";
import { ConsignSection } from "@/components/ConsignSection";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Section({ title, action, children }) {
  return (
    <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md">
      <div className="flex items-center justify-between p-4 border-b border-[#1A1A22]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{title}</p>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [saleOpen, setSaleOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [payPurchaseOpen, setPayPurchaseOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveBranch, setMoveBranch] = useState("");
  const [photoIdx, setPhotoIdx] = useState(0);
  const photoRef = useRef(null);

  const moveCar = async () => {
    if (!moveBranch || moveBranch === car.branch_id) return setMoveOpen(false);
    try {
      await api.post(`/cars/${car.id}/pindah-cabang`, { branch_id: moveBranch });
      toast.success("Lokasi stok berhasil dipindahkan");
      setMoveOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/cars/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `${files.length} foto ditambahkan` : "Foto mobil diperbarui");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      e.target.value = "";
    }
  };

  const deletePhoto = async (p) => {
    if (!window.confirm("Hapus foto ini?")) return;
    try {
      await api.delete(`/cars/${id}/photos`, { params: { path: p } });
      toast.success("Foto dihapus");
      setPhotoIdx(0);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const load = () => api.get(`/cars/${id}`).then((r) => setData(r.data)).catch(() => navigate("/mobil"));
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, [id]);

  const carArr = useMemo(() => (data ? [data.car] : []), [data]);

  if (!data) return <div className="h-96 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />;

  const { car, purchase, costs, sale, branch, payments = [], purchase_payments = [] } = data;
  const sisaHutang = purchase ? Math.max(0, purchase.price - (purchase.dp_amount || 0)) : 0;
  const purchaseBelumLunas = purchase && purchase.payment_type === "dp" && !purchase.paid_off;
  const totalBiaya = costs.reduce((a, c) => a + c.amount, 0);
  const hpp = (car.is_konsinyasi ? car.consign_price || 0 : purchase?.price || 0) + totalBiaya;
  const laba = sale ? sale.price - hpp - (sale.commission || 0) : null;
  const photos = car.photos?.length ? car.photos : (car.photo_path ? [car.photo_path] : []);

  const toggleStatus = async () => {
    const next = car.status === "dalam_persiapan" ? "siap_jual" : "dalam_persiapan";
    try {
      await api.put(`/cars/${car.id}`, { status: next });
      toast.success(next === "siap_jual" ? "Mobil ditandai siap jual" : "Mobil dikembalikan ke persiapan");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markPaid = () => setMarkPaidOpen(true);

  const togglePricelistVisibility = async () => {
    const isVisible = car.show_in_pricelist !== false;
    try {
      await api.put(`/cars/${car.id}`, { show_in_pricelist: !isVisible });
      toast.success(
        isVisible
          ? "Unit disembunyikan dari pricelist publik"
          : "Unit ditampilkan di pricelist publik"
      );
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeCar = async () => {
    if (!window.confirm("Hapus mobil ini beserta pembelian & biayanya?")) return;
    try {
      await api.delete(`/cars/${car.id}`);
      toast.success("Mobil dihapus");
      navigate("/mobil");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteCost = async (costId) => {
    if (!window.confirm("Hapus biaya ini?")) return;
    try {
      await api.delete(`/costs/${costId}`);
      toast.success("Biaya dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="car-detail-page" className="fade-up">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/mobil" data-testid="back-to-cars-link" className="p-2 rounded-md border border-[#1A1A22] text-zinc-400 hover:text-zinc-100 hover:bg-[#121218] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-50">{carLabel(car)}</h1>
        </div>
        <StatusBadge status={car.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-1 bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-hidden">
          <div className="aspect-[4/3] bg-[#121218]">
            {photos.length ? (
              <img src={fileUrl(photos[Math.min(photoIdx, photos.length - 1)], 1400)} alt={`${car.merk} ${car.model}`} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Car className="w-14 h-14 text-zinc-700" /></div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 p-2 overflow-x-auto border-t border-[#1A1A22]">
              {photos.map((p, i) => (
                <button key={p} data-testid={`photo-thumb-${i}`} onClick={() => setPhotoIdx(i)} className={`shrink-0 rounded-sm overflow-hidden ring-2 transition-colors duration-200 ${i === photoIdx ? "ring-blue-500" : "ring-transparent hover:ring-zinc-600"}`}>
                  <img src={fileUrl(p, 200)} alt="" loading="lazy" className="w-16 h-12 object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Plat Nomor</span><span className="text-zinc-200 font-medium">{car.plate || "-"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Warna</span><span className="text-zinc-200">{car.color || "-"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Kilometer</span><span className="text-zinc-200 font-mono">{new Intl.NumberFormat("id-ID").format(car.km || 0)} km</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Cabang</span><span className="text-zinc-200">{branch?.name || "-"}</span></div>
            {car.asking_cash_price ? <div className="flex justify-between"><span className="text-zinc-500">Harga Cash</span><Money value={car.asking_cash_price} className="text-blue-500" /></div> : null}
            {car.asking_credit_price ? <div className="flex justify-between"><span className="text-zinc-500">Harga Kredit</span><Money value={car.asking_credit_price} className="text-zinc-200" /></div> : null}
            {car.estimasi_biaya ? <div className="flex justify-between" data-testid="car-estimasi-detail"><span className="text-zinc-500">Estimasi Biaya</span><Money value={car.estimasi_biaya} className="text-amber-500" /></div> : null}
            {(car.credit_packages || []).length > 0 && (
              <div data-testid="car-credit-packages" className="pt-2 border-t border-[#1A1A22] space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Paket Kredit</p>
                {car.credit_packages.map((p, i) => (
                  <div key={i} data-testid={`car-credit-package-${i}`} className="bg-[#0F0F12] border border-[#1A1A22] rounded-md px-3 py-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-wider">{p.leasing_name || `Paket ${i + 1}`}</span>
                      <span className="text-xs text-zinc-300 font-mono">TDP {new Intl.NumberFormat("id-ID").format(p.tdp || 0)}</span>
                    </div>
                    {(p.installments || []).map((ins, j) => (
                      <p key={j} className="text-[11px] text-zinc-500 font-mono mt-0.5">Angsuran {new Intl.NumberFormat("id-ID").format(ins.amount || 0)} × {ins.tenor} bln</p>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {car.notes && <p className="text-xs text-zinc-500 pt-2 border-t border-[#1A1A22]">{car.notes}</p>}
          </div>
          <div className="p-4 pt-0 flex flex-wrap gap-2">
            {(can(user, "foto_mobil") || can(user, "kelola_mobil")) && (
              <>
                <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" data-testid="change-photo-input" onChange={uploadPhoto} />
                <Button data-testid="change-photo-button" size="sm" variant="outline" onClick={() => photoRef.current?.click()} className="border-[#26262E] text-zinc-300 hover:bg-[#121218]">
                  <ImagePlus className="w-3.5 h-3.5 mr-1" /> Tambah Foto
                </Button>
                {photos.length > 0 && (
                  <Button data-testid="delete-photo-button" size="sm" variant="outline" onClick={() => deletePhoto(photos[Math.min(photoIdx, photos.length - 1)])} className="border-rose-500/30 text-rose-500 hover:bg-rose-500/10">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Foto
                  </Button>
                )}
              </>
            )}
            {can(user, "kelola_mobil") && car.status !== "terjual" && (
              <Button
                data-testid="toggle-car-pricelist-button"
                size="sm"
                variant="outline"
                onClick={togglePricelistVisibility}
                className={car.show_in_pricelist !== false
                  ? "border-emerald-600/40 text-emerald-500 hover:bg-emerald-600/10"
                  : "border-[#26262E] text-zinc-300 hover:bg-[#121218]"}
              >
                {car.show_in_pricelist !== false ? (
                  <EyeOff className="w-3.5 h-3.5 mr-1" />
                ) : (
                  <Eye className="w-3.5 h-3.5 mr-1" />
                )}
                {car.show_in_pricelist !== false
                  ? "Sembunyikan Pricelist"
                  : "Tampilkan di Pricelist"}
              </Button>
            )}
            {can(user, "kelola_mobil") && !["terjual", "booking"].includes(car.status) && (
              <>
                <Button data-testid="edit-car-button" size="sm" variant="outline" onClick={() => setEditOpen(true)} className="border-[#26262E] text-zinc-300 hover:bg-[#121218]">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button data-testid="toggle-status-button" size="sm" onClick={toggleStatus} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
                  {car.status === "dalam_persiapan" ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tandai Siap Jual</> : <><Undo2 className="w-3.5 h-3.5 mr-1" /> Kembali ke Persiapan</>}
                </Button>
              </>
            )}
            {(can(user, "kelola_mobil") || can(user, "pindah_lokasi")) && (
              <Button data-testid="move-branch-button" size="sm" variant="outline" onClick={() => { setMoveBranch(car.branch_id); setMoveOpen(true); }} className="border-[#26262E] text-zinc-300 hover:bg-[#121218]">
                <MapPin className="w-3.5 h-3.5 mr-1" /> Pindah Cabang
              </Button>
            )}
            {user?.role === "admin" && !["terjual", "booking"].includes(car.status) && (
              <Button data-testid="delete-car-button" size="sm" variant="outline" onClick={removeCar} className="border-rose-500/30 text-rose-500 hover:bg-rose-500/10">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {car.is_konsinyasi && can(user, "lihat_hpp") && (
            <ConsignSection car={car} sale={sale} branches={branches} user={user} onSaved={load} />
          )}
          {(can(user, "lihat_hpp") || can(user, "kelola_pembelian")) && (
          <Section
            title="Pembelian"
            action={can(user, "kelola_pembelian") && (
              !purchase && !car.is_konsinyasi && car.status !== "terjual" ? (
                <Button data-testid="add-purchase-button" size="sm" onClick={() => setPurchaseOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
                  <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Catat Pembelian
                </Button>
              ) : purchaseBelumLunas ? (
                <Button data-testid="car-pay-purchase-button" size="sm" onClick={() => setPayPurchaseOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Bayar Cicilan
                </Button>
              ) : null
            )}
          >
            {!purchase ? (
              <p className="text-sm text-zinc-500">Belum ada catatan pembelian.</p>
            ) : (
              <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><p className="text-zinc-500 text-xs mb-1">Harga Beli</p><Money value={purchase.price} className="text-zinc-100 text-base" /></div>
                <div><p className="text-zinc-500 text-xs mb-1">Tipe</p><TypeBadge label={PURCHASE_PAYMENT_TYPES[purchase.payment_type]} /></div>
                {purchase.payment_type === "dp" && (
                  <div><p className="text-zinc-500 text-xs mb-1">Sudah Dibayar</p><Money value={purchase.dp_amount} className="text-zinc-100" />
                    <p className={`text-[10px] uppercase tracking-wider mt-1 ${purchase.paid_off ? "text-emerald-500" : "text-blue-500"}`}>{purchase.paid_off ? "Lunas" : "Belum Lunas"}</p>
                    {!purchase.paid_off && <p data-testid="car-purchase-sisa" className="text-[10px] text-zinc-500 font-mono">Sisa {new Intl.NumberFormat("id-ID").format(sisaHutang)}</p>}
                  </div>
                )}
                <div><p className="text-zinc-500 text-xs mb-1">Tanggal</p><p className="text-zinc-200">{fmtDate(purchase.date)}</p>
                  {purchase.seller_name && <p className="text-xs text-zinc-500 mt-1">dari {purchase.seller_name}</p>}
                </div>
                {purchase.tax_status && (
                  <div><p className="text-zinc-500 text-xs mb-1">Pajak</p>
                    <p className={`text-sm font-medium ${purchase.tax_status === "hidup" ? "text-emerald-500" : "text-rose-500"}`}>{purchase.tax_status === "hidup" ? "Hidup" : "Mati"}</p>
                    {purchase.tax_due_date && <p className="text-xs text-zinc-500">s.d. {purchase.tax_due_date}</p>}
                    {purchase.tax_amount ? <p className="text-xs text-zinc-500 font-mono">{new Intl.NumberFormat("id-ID").format(purchase.tax_amount)}</p> : null}
                  </div>
                )}
              </div>
              {purchase.payment_type === "dp" && purchase_payments.length > 0 && (
                <div data-testid="purchase-payments-list" className="border-t border-[#1A1A22] pt-3">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Riwayat Pembayaran ke Penjual</p>
                  <ul className="space-y-1 text-xs">
                    {purchase_payments.map((e) => (
                      <li key={e.id} className="flex justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-zinc-400 truncate">{fmtDate(e.date)} · {e.description.startsWith("Cicilan") ? `Cicilan${/\(([^)]*)\)$/.exec(e.description)?.[1] ? ` — ${/\(([^)]*)\)$/.exec(e.description)[1]}` : ""}` : "DP awal"}</p>
                          <p className="text-[11px] text-zinc-500">Keluar dari kas <span className="text-blue-400 font-medium">{e.branch_name || "-"}</span></p>
                        </div>
                        <Money value={e.amount} className="text-zinc-200 shrink-0" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              </div>
            )}
          </Section>
          )}

          <Section
            title="Biaya Mobil"
            action={can(user, "input_biaya_mobil") && (
              <Button data-testid="add-cost-button" size="sm" variant="outline" onClick={() => setCostOpen(true)} className="border-[#26262E] text-zinc-300 hover:bg-[#121218]">
                <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Biaya
              </Button>
            )}
          >
            {costs.length === 0 ? (
              <p className="text-sm text-zinc-500">Belum ada biaya (body repair, mesin, dokumen, dll).</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {costs.map((c) => (
                    <tr key={c.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                      <td className="py-2.5 pr-2"><TypeBadge label={COST_CATEGORIES[c.category]} /></td>
                      <td className="py-2.5 pr-2 text-zinc-400 text-xs">{c.description || "-"}<span className="block text-zinc-600">{fmtDate(c.date)}</span></td>
                      <td className="py-2.5 text-right"><Money value={c.amount} className="text-zinc-100" /></td>
                      {user?.role === "admin" && (
                        <td className="py-2.5 pl-2 w-8">
                          <button data-testid={`delete-cost-${c.id}`} onClick={() => deleteCost(c.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(purchase || car.is_konsinyasi) && can(user, "lihat_hpp") && (
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-[#1A1A22] text-sm">
                <span className="text-zinc-400">{car.is_konsinyasi ? "Modal Mobil (Harga Titip + Biaya)" : "Modal Mobil (Harga Beli + Biaya)"}</span>
                <Money value={hpp} className="text-blue-500 text-base font-semibold" />
              </div>
            )}
          </Section>

          <Section
            title="Penjualan"
            action={
              sale && sale.status !== "cancelled" ? (
                <div className="flex items-center gap-2">
                  {can(user, "input_penjualan") && (
                    <Button data-testid="car-receive-funds-button" size="sm" onClick={() => setReceiveOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Terima Dana
                    </Button>
                  )}
                  {can(user, "aksi_penjualan") && !sale.paid_off && (
                    <Button data-testid="car-mark-paid-button" size="sm" variant="outline" onClick={markPaid} className="border-emerald-600/40 text-emerald-500 hover:bg-emerald-600/10 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tandai Lunas
                    </Button>
                  )}
                  {(can(user, "aksi_penjualan") || can(user, "refund_penjualan")) && (user?.role !== "admin_cabang" || sale.created_by === user?.id) && (
                    <Button data-testid="car-refund-sale-button" size="sm" variant="outline" onClick={() => setRefundOpen(true)} className="border-rose-600/40 text-rose-400 hover:bg-rose-600/10 font-semibold">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Refund
                    </Button>
                  )}
                </div>
              ) : (
                !sale && (purchase || car.is_konsinyasi || !can(user, "lihat_hpp")) && car.status !== "terjual" && can(user, "input_penjualan") && (
                  <Button data-testid="add-sale-button" size="sm" onClick={() => setSaleOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Catat Penjualan
                  </Button>
                )
              )
            }
          >
            {!sale ? (
              <p className="text-sm text-zinc-500">Mobil belum terjual.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-zinc-500 text-xs mb-1">Harga Jual</p>
                    {sale.price ? <Money value={sale.price} className="text-emerald-500 text-base" /> : <span className="text-blue-500 text-xs font-semibold uppercase tracking-wider">Belum Ditentukan</span>}
                    {(sale.received_amount || 0) > 0 && <p className="text-[10px] text-zinc-500 mt-1 font-mono">Dana diterima: {new Intl.NumberFormat("id-ID").format(sale.received_amount)}</p>}
                  </div>
                  <div><p className="text-zinc-500 text-xs mb-1">Tipe</p><TypeBadge label={PAYMENT_TYPES[sale.payment_type]} />
                    {sale.leasing_name && <p className="text-xs text-zinc-500 mt-1">{sale.leasing_name}</p>}
                  </div>
                  <div><p className="text-zinc-500 text-xs mb-1">Pembeli</p><p className="text-zinc-200">{sale.buyer_name}</p><p className="text-xs text-zinc-500">{fmtDate(sale.date)}</p></div>
                  <div><p className="text-zinc-500 text-xs mb-1">Komisi Penjual</p><Money value={sale.commission} className="text-zinc-100" />
                    {sale.salesperson_name && <p className="text-xs text-zinc-500 mt-1">{sale.salesperson_name}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm pt-3 border-t border-[#1A1A22]">
                  <div><p className="text-zinc-500 text-xs mb-1">Terjual di Cabang</p><p data-testid="sale-branch-name" className="text-zinc-200">{sale.sale_branch_name || "-"}</p></div>
                  <div><p className="text-zinc-500 text-xs mb-1">Kas Penerima</p><p data-testid="sale-cash-branch-name" className="text-zinc-200">{sale.cash_branch_name || "-"}</p></div>
                  <div><p className="text-zinc-500 text-xs mb-1">Diinput oleh</p><p data-testid="sale-created-by" className="text-zinc-200">{sale.created_by_name || "-"}</p><p className="text-xs text-zinc-500">{sale.created_at ? fmtDate(sale.created_at.slice(0, 10)) : ""}</p></div>
                </div>
                {sale.price > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400">Dana Diterima / Sisa</span>
                    <span className="font-mono tabular-nums text-zinc-200">
                      {new Intl.NumberFormat("id-ID").format(sale.received_amount || 0)}
                      <span className="text-zinc-500"> / sisa </span>
                      <span className={sale.price - (sale.received_amount || 0) > 0 ? "text-blue-500" : "text-emerald-500"}>{new Intl.NumberFormat("id-ID").format(Math.max(0, sale.price - (sale.received_amount || 0)))}</span>
                    </span>
                  </div>
                )}
                {payments.length > 0 && (
                  <div className="pt-2 border-t border-[#1A1A22]">
                    <p className="text-zinc-500 text-xs mb-2">Riwayat Pembayaran</p>
                    <div data-testid="sale-payments-list" className="space-y-2">
                      {payments.map((p) => (
                        <div key={p.id} className="flex justify-between gap-2 text-xs">
                          <div className="min-w-0">
                            <p className="text-zinc-300 truncate">{fmtDate(p.date)} · {p.description}</p>
                            <p className="text-[11px] text-zinc-500">{p.type === "masuk" ? "Masuk ke kas" : "Keluar dari kas"} <span className="text-blue-400 font-medium">{p.branch_name || "-"}</span></p>
                          </div>
                          <span className={`font-mono tabular-nums whitespace-nowrap ${p.type === "masuk" ? "text-emerald-500" : "text-rose-500"}`}>{p.type === "masuk" ? "+" : "-"}{new Intl.NumberFormat("id-ID").format(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {can(user, "lihat_hpp") && (
                <div className="flex justify-between items-center pt-3 border-t border-[#1A1A22] text-sm">
                  <span className="text-zinc-400">Laba Bersih Mobil Ini</span>
                  {sale.price ? <Money value={laba} className={`text-lg font-semibold ${laba >= 0 ? "text-emerald-500" : "text-rose-500"}`} /> : <span className="text-xs text-zinc-500">Menunggu harga final</span>}
                </div>
                )}
              </div>
            )}
          </Section>
        </div>
      </div>

      <CarFormDialog open={editOpen} onOpenChange={setEditOpen} branches={branches} car={car} onSaved={load} />
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Pindah Lokasi Stok</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400 -mt-2">{carLabel(car)}</p>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Cabang Tujuan</p>
            <Select value={moveBranch} onValueChange={setMoveBranch}>
              <SelectTrigger data-testid="move-branch-select" className="h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.id === car.branch_id ? " (saat ini)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="move-branch-submit" onClick={moveCar} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            Pindahkan
          </Button>
        </DialogContent>
      </Dialog>
      <PurchaseDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} cars={carArr} fixedCar={car} onSaved={load} />
      <CostDialog open={costOpen} onOpenChange={setCostOpen} cars={carArr} fixedCar={car} onSaved={load} />
      <SaleDialog open={saleOpen} onOpenChange={setSaleOpen} cars={carArr} fixedCar={car} onSaved={load} />
      <ReceiveDialog sale={receiveOpen && sale ? { ...sale, car_name: carLabel(car) } : null} branches={branches} onClose={() => setReceiveOpen(false)} onSaved={load} />
      <PayPurchaseDialog purchase={payPurchaseOpen && purchase ? { ...purchase, car_name: carLabel(car), branch_id: car.branch_id } : null} branches={branches} onClose={() => setPayPurchaseOpen(false)} onSaved={load} />
      <RefundDialog sale={refundOpen && sale ? { ...sale, car_name: carLabel(car) } : null} branches={branches} onClose={() => setRefundOpen(false)} onSaved={load} />
      <MarkPaidDialog
        sale={markPaidOpen && sale ? { ...sale, car_name: carLabel(car) } : null}
        onClose={() => setMarkPaidOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
